import { useEffect, useState } from 'react'

import * as canIUse from '$extensionSrc/utils/canIUse'
import { EXTENSION_MESSAGE_TYPES } from '$extensionSrc/utils/enums'
import { createMessage } from '$extensionSrc/utils/messages'
import { isSidePanelContext } from '$extensionSrc/utils/webextSidePanelApi'

type WhereAmIRunning = {
  /**
   * False until values have been determined for all other variables.
   */
  isLoading: boolean | undefined

  /**
   * True if the code is running in the side panel; we currently only differentiate
   * between side panel and content scripts.
   */
  isSidePanel: boolean | undefined

  /**
   * In the side panel, the active tab in the current window (updated w/ listeners).
   *
   * In a content script, the tab in which the script was injected.
   */
  currentTab: chrome.tabs.Tab | undefined

  /**
   * The id of the browser window this code is running in; will not update if
   * the tab is moved to a different window.
   */
  currentWindowId: number | undefined
}

export default function useWhereAmIRunning(): WhereAmIRunning {
  const [isSidePanel, setIsSidePanel] = useState<boolean>(undefined)
  const [currentTab, setCurrentTab] = useState<chrome.tabs.Tab>(undefined)
  const [currentWindowId, setCurrentWindowId] = useState<number>(undefined)

  useEffect(() => {
    if (canIUse.chromeWindows()) {
      chrome.windows.getCurrent().then((w) => setCurrentWindowId(w.id))
    }
  }, [])

  useEffect(() => {
    isSidePanelContext().then((x) => setIsSidePanel(x))
  }, [])

  useEffect(() => {
    async function initializeCurrentTab() {
      if (isSidePanel) {
        const currentActiveTab = (
          await chrome.tabs.query({ windowId: currentWindowId, active: true })
        )[0]
        setCurrentTab(currentActiveTab)
      } else {
        const message = createMessage(
          EXTENSION_MESSAGE_TYPES.GET_CURRENT_TAB,
          {},
        )
        const response = await chrome.runtime.sendMessage(message)
        setCurrentTab(response?.tab)
      }
    }
    initializeCurrentTab()
  }, [currentWindowId, isSidePanel])

  useEffect(() => {
    async function onUpdatedListenerFn(tabId, changeInfo, tab) {
      if (tab.windowId === currentWindowId) {
        const currentActiveTab = (
          await chrome.tabs.query({
            windowId: currentWindowId,
            active: true,
          })
        )[0]
        setCurrentTab(currentActiveTab)
      }
    }

    async function onActivatedListenerFn({
      tabId,
      windowId,
    }: {
      tabId: number
      windowId: number
    }) {
      if (windowId === currentWindowId) {
        const currentActiveTab = await chrome.tabs.get(tabId)
        setCurrentTab(currentActiveTab)
      }
    }

    if (isSidePanel) {
      chrome.tabs.onUpdated.addListener(onUpdatedListenerFn)
      chrome.tabs.onActivated.addListener(onActivatedListenerFn)
      return () => {
        chrome.tabs.onUpdated.removeListener(onUpdatedListenerFn)
        chrome.tabs.onActivated.removeListener(onActivatedListenerFn)
      }
    }
    return () => {}
  }, [currentWindowId, isSidePanel])

  const isLoading =
    isSidePanel === undefined ||
    currentTab === undefined ||
    currentWindowId === undefined

  return {
    isLoading,
    isSidePanel,
    currentTab,
    currentWindowId,
  }
}
